import update from "immutability-helper";
import orderBy from "lodash.orderby";

const normalizeCellCoordinates = (cell) => ({
  ...cell,
  row: Number(cell.row),
  col: Number(cell.col),
});

export default function tablesReducer(
  state = {
    cells: {},
  },
  action
) {
  try {
    if (action.type === "SET_TABLE") {
      const { table, registryVersion } = action.payload;
      if (table)
        return update(state, {
          [table.id]: { $auto: { [registryVersion]: { $set: table } } },
        });
      else return state;
    } else if (action.type === "DELETE_TABLE") {
      const { registryVersion, tableId } = action.payload;
      return update(state, {
        [tableId]: { $auto: { [registryVersion]: { $set: null } } },
        cells: {
          [tableId]: { $auto: { [registryVersion]: { $set: null } } },
        },
      });
    } else if (action.type === "REPLACE_TABLE") {
      const { table, registryVersion } = action.payload;
      if (table)
        return update(state, {
          [table.id]: { $auto: { [registryVersion]: { $set: table } } },
          cells: {
            [table.id]: (tmpTable) =>
              update(tmpTable || {}, {
                [registryVersion]: {
                  $set: orderBy(
                    (table.cells || []).map(normalizeCellCoordinates),
                    ["row", "col"],
                    ["asc", "asc"]
                  ),
                },
              }),
          },
        });
      else return state;
    } else if (action.type === "SET_TABLE_CELLS") {
      const { cells, registryVersion, tableId } = action.payload;
      return update(state, {
        cells: {
          [tableId]: (tmpTable) =>
            update(tmpTable || {}, {
              [registryVersion]: {
                $set: orderBy(
                  (cells || []).map(normalizeCellCoordinates),
                  ["row", "col"],
                  ["asc", "asc"]
                ),
              },
            }),
        },
      });
    } else if (action.type === "REPLACE_CELL") {
      const { cell, registryVersion, tableId } = action.payload;
      return update(state, {
        cells: {
          [tableId]: (tmpTable) =>
            update(tmpTable || {}, {
              [registryVersion]: {
                $apply: (_cells) => {
                  const normalizedCell = normalizeCellCoordinates(cell);
                  const cellIndex = _cells.findIndex(
                    (x) =>
                      Number(x.row) === normalizedCell.row &&
                      Number(x.col) === normalizedCell.col
                  );
                  const newCells =
                    cellIndex !== -1
                      ? update(_cells, { [cellIndex]: { $set: normalizedCell } })
                      : update(_cells, { $push: [normalizedCell] });
                  return orderBy(newCells, ["row", "col"], ["asc", "asc"]);
                },
              },
            }),
        },
      });
    } else if (action.type === "ADD_ROW") {
      // increase tables row count and increase row num if cell.row >= row
      const { row, registryVersion, tableId } = action.payload;
      const normalizedRow = Number(row);
      return update(state, {
        [tableId]: {
          $auto: {
            [registryVersion]: {
              rowCount: { $apply: (x) => x + 1 },
            },
          },
        },
        cells: {
          [tableId]: (tmpTable) =>
            update(tmpTable || {}, {
              [registryVersion]: {
                $apply: (_cells) =>
                  _cells.map((x) =>
                    Number(x.row) > normalizedRow
                      ? { ...x, row: Number(x.row) + 1 }
                      : normalizeCellCoordinates(x)
                  ),
              },
            }),
        },
      });
    } else if (action.type === "ADD_COLUMN") {
      // increase tables col count, add column width and increase col num if cell.col > col
      // if col === 0 add to the front
      const { col, registryVersion, tableId } = action.payload;
      const normalizedCol = Number(col);
      return update(state, {
        [tableId]: {
          $auto: {
            [registryVersion]: {
              colCount: { $apply: (x) => x + 1 },
              //colLen: "22,99,117",
              colLen: {
                $apply: (x) => {
                  let _x = x.split(",");
                  _x.splice(normalizedCol, 0, "40");
                  return _x.join(",");
                },
              },
            },
          },
        },
        cells: {
          [tableId]: (tmpTable) =>
            update(tmpTable || {}, {
              [registryVersion]: {
                $apply: (_cells) =>
                  _cells.map((x) =>
                    Number(x.col) > normalizedCol
                      ? { ...x, col: Number(x.col) + 1 }
                      : normalizeCellCoordinates(x)
                  ),
              },
            }),
        },
      });
    } else if (action.type === "DELETE_ROW") {
      const { row, registryVersion, tableId } = action.payload;
      const normalizedRow = Number(row);
      if (state[registryVersion].rowCount < 2) {
        return state;
      }
      return update(state, {
        [tableId]: {
          $auto: {
            [registryVersion]: {
              rowCount: { $apply: (x) => x - 1 },
            },
          },
        },
        cells: {
          [tableId]: (tmpTable) =>
            update(tmpTable || {}, {
              [registryVersion]: {
                $apply: (_cells) =>
                  _cells.reduce((acc, cur) => {
                    const normalizedCur = normalizeCellCoordinates(cur);
                    if (normalizedCur.row < normalizedRow) {
                      acc.push(normalizedCur);
                    } else if (normalizedCur.row > normalizedRow) {
                      acc.push({ ...normalizedCur, row: normalizedCur.row - 1 });
                    }
                    return acc;
                  }, []),
              },
            }),
        },
      });
    } else if (action.type === "DELETE_COLUMN") {
      const { col, registryVersion, tableId } = action.payload;
      const normalizedCol = Number(col);
      if (state[registryVersion].colCount < 2) {
        return state;
      }
      return update(state, {
        [tableId]: {
          $auto: {
            [registryVersion]: {
              colCount: { $apply: (x) => x - 1 },
              //colLen: "22,99,117",
              colLen: {
                $apply: (x) => {
                  let _x = x.split(",");
                  _x.splice(normalizedCol - 1, 1);
                  return _x.join(",");
                },
              },
            },
          },
        },
        cells: {
          [tableId]: (tmpTable) =>
            update(tmpTable || {}, {
              [registryVersion]: {
                $apply: (_cells) =>
                  _cells.reduce((acc, cur) => {
                    const normalizedCur = normalizeCellCoordinates(cur);
                    if (normalizedCur.col < normalizedCol) {
                      acc.push(normalizedCur);
                    } else if (normalizedCur.col > normalizedCol) {
                      acc.push({ ...normalizedCur, col: normalizedCur.col - 1 });
                    }
                    return acc;
                  }, []),
              },
            }),
        },
      });
    } else if (action.type === "ADD_CELLS") {
      const { registryVersion, tableId } = action.payload;

      let table = state[tableId][registryVersion];
      let cells = state.cells[tableId][registryVersion];

      if (table && action.payload.cells.length > 0) {
        let maxRow = table.rowCount;
        let maxCol = table.colCount;

        action.payload.cells.forEach((cell) => {
          const normalizedCell = normalizeCellCoordinates(cell);

          if (normalizedCell.row > maxRow) maxRow = normalizedCell.row;
          if (normalizedCell.col > maxCol) maxCol = normalizedCell.col;

          const cellIndex = cells.findIndex(
            (x) =>
              Number(x.col) === normalizedCell.col &&
              Number(x.row) === normalizedCell.row
          );

          // swap the value into nodeText so the field will be evaluated in backend
          const _cell = {
            ...normalizedCell,
            nodeText: { name: normalizedCell.value },
            value: undefined,
          };

          // if cell has value we can update it, otherwise remove it since we don't keep empty cells
          if (cellIndex !== -1) {
            if (normalizedCell.value) {
              cells = update(cells, {
                [cellIndex]: {
                  $set: _cell,
                },
              });
            } else {
              cells = update(cells, {
                $splice: [[cellIndex, 1]],
              });
            }
          } else if (normalizedCell.value) {
            cells = update(cells, { $push: [_cell] });
          }
        });

        return update(state, {
          [tableId]: {
            $auto: {
              [registryVersion]: {
                rowCount: { $set: maxRow },
                colCount: { $set: maxCol },
                colLen: {
                  $apply: (x) => {
                    let newColLenArr = [];
                    const splitColLen = x.split(",");
                    for (let i = 0; i < maxCol; i++) {
                      const colLen = splitColLen[i];
                      newColLenArr.push(colLen || "40");
                    }
                    return newColLenArr.join(",");
                  },
                },
              },
            },
          },
          cells: {
            [tableId]: (tmpTable) =>
              update(tmpTable || {}, {
                [registryVersion]: {
                  $set: cells,
                },
              }),
          },
        });
      } else {
        return state;
      }
    }

    return state;
  } catch (error) {
    console.error(
      `Error: ${error}, Reducer: TableCellsReducer, Action: ${
        action.type
      }, Payload: ${
        action.payload ? JSON.stringify(action.payload) : "no payload"
      }`,
      state
    );
    return state;
  }
}
